import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, Chip, Container, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import { calculateMockRiskLevel } from 'config/api/dpias/dpias.helpers';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import { canAccessAdminDpiaReviewPage } from '../_shared/assetRopaReviewAccess';
import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import {
  buildBlockingApprovalErrors,
  buildReviewSummary,
  buildReviewWarnings,
  canMakeReviewDecision,
  hasClarificationHistory,
  isChangedAfterSubmission,
  requiresReviewCommentForApproval,
} from './adminDpiaReview.helpers';
import useAdminDpiaReview from './useAdminDpiaReview';

type RouteParams = {
  dpiaId: string;
};

type LocationState = {
  returnPath?: string;
};

const formatTimestamp = (value: string | null) => {
  if (!value) return '—';

  try {
    return format(new Date(value), 'yyyy-MM-dd HH:mm');
  } catch {
    return value;
  }
};

const AdminDpiaReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { dpiaId } = useParams<RouteParams>();
  const { enqueueSnackbar } = useSnackbar();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canAccess = canAccessAdminDpiaReviewPage(userData);
  const ownerMessages = processing_activities_messages.asset_ropa.owner_dpia;
  const messages = processing_activities_messages.asset_ropa.admin_dpia_review;
  const [expandedRiskIds, setExpandedRiskIds] = useState<number[]>([]);

  const returnPath = location.state?.returnPath ?? PATHS.PROCESSING_ACTIVITIES_ASSET_REVIEWS;

  const navigateBack = useCallback(() => {
    history.push(returnPath);
  }, [history, returnPath]);

  const validationMessages = useMemo(
    () => ({
      required: t(validation_messages.required),
      purposeScope: t(ownerMessages.validation.purpose_scope),
      riskTitle: t(ownerMessages.validation.risk_title),
      riskDescription: t(ownerMessages.validation.risk_description),
      riskAffectedRights: t(ownerMessages.validation.risk_affected_rights),
      riskLikelihood: t(ownerMessages.validation.risk_likelihood),
      riskImpact: t(ownerMessages.validation.risk_impact),
      riskMeasures: t(ownerMessages.validation.risk_measures),
      measureTitle: t(ownerMessages.validation.measure_title),
      measureDescription: t(ownerMessages.validation.measure_description),
      measureResponsible: t(ownerMessages.validation.measure_responsible),
      measureStatus: t(ownerMessages.validation.measure_status),
      clarificationResponse: t(ownerMessages.validation.clarification_response),
      residualRisk: t(ownerMessages.validation.residual_risk),
      priorConsultation: t(ownerMessages.validation.prior_consultation),
      changedAfterSubmission: t(messages.validation.changed_after_submission),
      incompleteApproval: t(messages.validation.incomplete_approval),
      clarificationComment: t(messages.validation.clarification_comment),
      highRiskReviewComment: t(messages.validation.high_risk_review_comment),
    }),
    [messages, ownerMessages, t],
  );

  const warningMessages = useMemo(
    () => ({
      changedAfterSubmission: t(messages.validation.changed_after_submission),
      highResidualRisk: t(messages.warnings.high_residual_risk),
      priorConsultationMayBeRequired: t(messages.warnings.prior_consultation_may_be_required),
      priorConsultationRequired: t(messages.warnings.prior_consultation_required),
      openMeasures: t(messages.warnings.open_measures),
      thirdCountryProcessing: t(messages.warnings.third_country_processing),
    }),
    [messages, t],
  );

  const {
    context,
    detail,
    formValues,
    handleApprove,
    handleRequestClarification,
    isApproving,
    isError,
    isLoading,
    isRequestingClarification,
    refetch,
    reviewComment,
    setReviewComment,
    setValidationErrors,
    validationErrors,
  } = useAdminDpiaReview({
    dpiaId: Number(dpiaId),
    onApproveSuccess: () => {
      enqueueSnackbar(t(messages.toasts.approved), { variant: 'success' });
      navigateBack();
    },
    onClarificationSuccess: () => {
      enqueueSnackbar(t(messages.toasts.clarification_requested), { variant: 'success' });
      navigateBack();
    },
    organizationId,
  });

  const reviewSummary = formValues ? buildReviewSummary(formValues) : null;
  const reviewWarnings = detail && context && formValues ? buildReviewWarnings(detail, context, formValues, warningMessages) : [];
  const blockingErrors = detail && formValues ? buildBlockingApprovalErrors(detail, formValues, validationMessages) : [];
  const canDecide = detail ? canMakeReviewDecision(detail.status) : false;
  const changedAfterSubmission = detail ? isChangedAfterSubmission(detail) : false;
  const showClarificationHistory = detail ? hasClarificationHistory(detail) : false;

  const handleApproveClick = useCallback(async () => {
    try {
      await handleApprove(validationMessages);
    } catch {
      if (validationErrors.approval || validationErrors.reviewComment) return;
      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    }
  }, [enqueueSnackbar, handleApprove, t, validationErrors.approval, validationErrors.reviewComment, validationMessages]);

  const handleClarificationClick = useCallback(async () => {
    try {
      await handleRequestClarification(validationMessages);
    } catch {
      if (validationErrors.reviewComment) return;
      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    }
  }, [enqueueSnackbar, handleRequestClarification, t, validationErrors.reviewComment, validationMessages]);

  let content: React.ReactNode;

  if (!canAccess) {
    content = (
      <Typography color='textSecondary' variant='body2'>
        {t(processing_activities_messages.asset_ropa.forbidden_body)}
      </Typography>
    );
  } else if (isLoading || !formValues || !detail || !context) {
    content = <Loader inner />;
  } else if (isError) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' variant='body2'>
          {t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>
      </Box>
    );
  } else {
    content = (
      <>
        <Box className={styles.reviewPanel}>
          <Typography variant='h3'>{detail.title}</Typography>
          <div className={styles.reviewMeta}>
            <span>
              <strong>{t(messages.status)}:</strong>{' '}
              <span className={`${styles.statusBadge} ${styles.statusBadgeInfo}`}>{t(ownerMessages.statuses[detail.status])}</span>
            </span>
            <span>
              <strong>{t(messages.responsible_owner)}:</strong> {detail.responsibleName}
            </span>
            <span>
              <strong>{t(messages.submitted)}:</strong> {formatTimestamp(detail.submittedAt)}
            </span>
            <span>
              <strong>{t(messages.submitted_revision)}:</strong> {detail.submittedRevision ?? '—'}
            </span>
            <span>
              <strong>{t(messages.current_revision)}:</strong> {detail.revision}
            </span>
          </div>
        </Box>

        <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
          {t(messages.truth_note)}
        </Typography>

        <Box className={styles.reviewPanel} style={{ backgroundColor: '#f3f8fc', borderColor: '#c9dff1' }}>
          <Typography variant='body2'>
            <strong>{t(messages.integrity_banner.title)}</strong> {t(messages.integrity_banner.body)}
          </Typography>
          {changedAfterSubmission && (
            <Typography color='error' style={{ marginTop: 8 }} variant='body2'>
              {t(messages.changed_after_submission_badge)}
            </Typography>
          )}
        </Box>

        <div className={styles.twoColumnLayout}>
          <aside>
            <Box className={styles.sidePanel}>
              <div className={styles.reviewEyebrow}>{t(ownerMessages.side_panel.eyebrow)}</div>
              <Typography gutterBottom variant='h3'>
                {t(ownerMessages.side_panel.title)}
              </Typography>
              <div className={styles.fieldList}>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.asset)}</div>
                  <div>{context.assetName}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.processing_activity)}</div>
                  <div>{context.assetProcessingActivityName}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.asset_type)}</div>
                  <div>{context.assetTypeName}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.owner)}</div>
                  <div>{context.operationalOwnerName ?? '—'}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.data_subjects)}</div>
                  <div>{context.dataSubjectCategories.join(', ')}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.personal_data)}</div>
                  <div>{context.personalDataCategories.join(', ')}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.countries)}</div>
                  <div>{context.countryHandling.join(', ')}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.minimum_security)}</div>
                  <div>{context.minimumSecurityAssessment}</div>
                </div>
              </div>

              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(ownerMessages.side_panel.purpose_scope)}
              </Typography>
              {context.availablePurposes.map(purpose => (
                <Box key={purpose.id} className={styles.purposePanel}>
                  <Typography variant='body2'>
                    <strong>{purpose.name}</strong>
                  </Typography>
                  <Typography color='textSecondary' variant='caption'>
                    {purpose.legalBasis}
                  </Typography>
                  <Typography variant='body2'>{purpose.applicationDescription}</Typography>
                </Box>
              ))}

              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(ownerMessages.side_panel.source_request)}
              </Typography>
              <Typography variant='body2'>
                <strong>{t(ownerMessages.side_panel.recommendation)}:</strong> {context.sourceRequest.recommendation}
              </Typography>
              <Typography variant='body2'>
                <strong>{t(ownerMessages.side_panel.decision)}:</strong> {context.sourceRequest.decision}
              </Typography>
              <Typography color='textSecondary' variant='body2'>
                {context.sourceRequest.decisionComment}
              </Typography>
            </Box>
          </aside>

          <section className={styles.mainPanel}>
            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 1</div>
              <Typography gutterBottom variant='h3'>
                {t(ownerMessages.sections.scope.title)}
              </Typography>
              {(
                [
                  ['plannedProcessingDescription', ownerMessages.sections.scope.planned_processing],
                  ['assessmentScope', ownerMessages.sections.scope.assessment_scope],
                  ['projectOrChangeContext', ownerMessages.sections.scope.project_context],
                ] as const
              ).map(([field, label]) => (
                <TextField
                  key={field}
                  disabled
                  fullWidth
                  label={t(label)}
                  margin='normal'
                  multiline
                  rows={field === 'plannedProcessingDescription' ? 4 : 3}
                  value={formValues[field]}
                  variant='outlined'
                />
              ))}
              <Typography gutterBottom variant='body2'>
                {t(ownerMessages.sections.scope.purposes)}
              </Typography>
              <div className={styles.reviewActions}>
                {context.availablePurposes
                  .filter(purpose => formValues.selectedPurposeIds.includes(purpose.id))
                  .map(purpose => (
                    <Chip key={purpose.id} color='primary' label={purpose.name} />
                  ))}
              </div>
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 2</div>
              <Typography gutterBottom variant='h3'>
                {t(ownerMessages.sections.necessity.title)}
              </Typography>
              {(
                ['necessityAssessment', 'proportionalityAssessment', 'dataMinimisationAssessment', 'retentionAndAccessAssessment'] as const
              ).map(field => (
                <TextField
                  key={field}
                  disabled
                  fullWidth
                  label={t(ownerMessages.sections.necessity[field])}
                  margin='normal'
                  multiline
                  rows={3}
                  value={formValues[field]}
                  variant='outlined'
                />
              ))}
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewPanelHeader}>
                <div>
                  <div className={styles.reviewEyebrow}>SECTION 3</div>
                  <Typography variant='h3'>{t(ownerMessages.sections.risks.title)}</Typography>
                  <Typography color='textSecondary' variant='body2'>
                    {t(ownerMessages.sections.risks.help)}
                  </Typography>
                </div>
              </div>
              {formValues.risks.map((risk, riskIndex) => {
                const expanded = expandedRiskIds.includes(risk.id) || riskIndex === 0;
                const riskLevel =
                  calculateMockRiskLevel(risk.likelihood, risk.impact) || risk.riskLevel || t(ownerMessages.sections.risks.not_assessed);

                return (
                  <Box key={risk.id} className={styles.purposePanel}>
                    <div className={styles.reviewPanelHeader}>
                      <div>
                        <Typography variant='body1'>
                          <strong>{risk.title || t(ownerMessages.sections.risks.untitled)}</strong>
                        </Typography>
                        <Typography color='textSecondary' variant='caption'>
                          {t(ownerMessages.sections.risks.likelihood)}:{' '}
                          {risk.likelihood ? t(ownerMessages.likelihood[risk.likelihood]) : '—'} · {t(ownerMessages.sections.risks.impact)}:{' '}
                          {risk.impact ? t(ownerMessages.impact[risk.impact]) : '—'} · {riskLevel}
                        </Typography>
                      </div>
                      <Button
                        onClick={() =>
                          setExpandedRiskIds(current => (expanded ? current.filter(id => id !== risk.id) : [...current, risk.id]))
                        }
                      >
                        {expanded ? t(ownerMessages.sections.risks.collapse) : t(ownerMessages.sections.risks.expand)}
                      </Button>
                    </div>
                    {expanded && (
                      <>
                        <TextField
                          disabled
                          fullWidth
                          label={t(ownerMessages.sections.risks.risk_description)}
                          margin='normal'
                          multiline
                          rows={3}
                          value={risk.description}
                          variant='outlined'
                        />
                        <TextField
                          disabled
                          fullWidth
                          label={t(ownerMessages.sections.risks.affected_rights)}
                          margin='normal'
                          multiline
                          rows={3}
                          value={risk.affectedRightsAndFreedoms}
                          variant='outlined'
                        />
                        <Typography gutterBottom variant='h4'>
                          {t(ownerMessages.sections.risks.measures)}
                        </Typography>
                        {risk.measures.map(measure => (
                          <Box key={measure.id} className={styles.purposePanel}>
                            <div className={styles.reviewPanelHeader}>
                              <div>
                                <Typography variant='body2'>
                                  <strong>{measure.title}</strong>
                                </Typography>
                                <Typography color='textSecondary' variant='caption'>
                                  {measure.description}
                                </Typography>
                              </div>
                              <div className={styles.reviewActions}>
                                <Chip label={t(ownerMessages.measure_status[measure.status])} size='small' />
                                <Chip label={measure.responsibleName} size='small' variant='outlined' />
                              </div>
                            </div>
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 4</div>
              <Typography gutterBottom variant='h3'>
                {t(ownerMessages.sections.overall.title)}
              </Typography>
              <TextField
                disabled
                fullWidth
                label={t(ownerMessages.sections.overall.overall_assessment)}
                margin='normal'
                multiline
                rows={4}
                value={formValues.overallAssessment}
                variant='outlined'
              />
              <div className={styles.filtersRow}>
                <FormControl disabled fullWidth margin='normal' variant='outlined'>
                  <InputLabel>{t(ownerMessages.sections.overall.residual_risk)}</InputLabel>
                  <Select label={t(ownerMessages.sections.overall.residual_risk)} value={formValues.residualRiskLevel}>
                    {Object.keys(ownerMessages.residual_risk).map(option => (
                      <MenuItem key={option} value={option}>
                        {t(ownerMessages.residual_risk[option as keyof typeof ownerMessages.residual_risk])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl disabled fullWidth margin='normal' variant='outlined'>
                  <InputLabel>{t(ownerMessages.sections.overall.prior_consultation)}</InputLabel>
                  <Select label={t(ownerMessages.sections.overall.prior_consultation)} value={formValues.priorConsultationAssessment}>
                    {Object.keys(ownerMessages.prior_consultation).map(option => (
                      <MenuItem key={option} value={option}>
                        {t(ownerMessages.prior_consultation[option as keyof typeof ownerMessages.prior_consultation])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <TextField
                disabled
                fullWidth
                label={t(ownerMessages.sections.overall.owner_conclusion)}
                margin='normal'
                multiline
                rows={3}
                value={formValues.ownerConclusion}
                variant='outlined'
              />
            </Box>

            {showClarificationHistory && (
              <Box className={styles.apaSection}>
                <div className={styles.reviewEyebrow}>{t(messages.sections.clarification_history.eyebrow)}</div>
                <Typography gutterBottom variant='h3'>
                  {t(messages.sections.clarification_history.title)}
                </Typography>
                {detail.administratorClarificationComment && (
                  <Box className={styles.errorBanner} style={{ backgroundColor: '#fffaf0', borderColor: '#f0d7a0' }}>
                    <Typography color='textSecondary' variant='caption'>
                      {t(messages.sections.clarification_history.requested_at, {
                        date: formatTimestamp(detail.clarificationRequestedAt),
                        name: detail.clarificationRequestedByName ?? '—',
                      })}
                    </Typography>
                    <Typography style={{ marginTop: 8, whiteSpace: 'pre-wrap' }} variant='body2'>
                      {detail.administratorClarificationComment}
                    </Typography>
                  </Box>
                )}
                {detail.ownerResponseComment && (
                  <TextField
                    disabled
                    fullWidth
                    label={t(messages.sections.clarification_history.owner_response)}
                    margin='normal'
                    multiline
                    rows={4}
                    value={detail.ownerResponseComment}
                    variant='outlined'
                  />
                )}
              </Box>
            )}

            {reviewSummary && (
              <Box className={styles.apaSection}>
                <div className={styles.reviewEyebrow}>{t(messages.sections.summary.eyebrow)}</div>
                <Typography gutterBottom variant='h3'>
                  {t(messages.sections.summary.title)}
                </Typography>
                <Typography color='textSecondary' gutterBottom variant='body2'>
                  {t(messages.sections.summary.help)}
                </Typography>
                <div className={styles.metricsRow}>
                  <Box className={styles.metricCard}>
                    <div className={styles.metricLabel}>{t(messages.sections.summary.documented_risks)}</div>
                    <div className={styles.metricValue}>{reviewSummary.documentedRisks}</div>
                  </Box>
                  <Box className={styles.metricCard}>
                    <div className={styles.metricLabel}>{t(messages.sections.summary.high_risks)}</div>
                    <div className={styles.metricValue}>{reviewSummary.highOrVeryHighRisks}</div>
                  </Box>
                  <Box className={styles.metricCard}>
                    <div className={styles.metricLabel}>{t(messages.sections.summary.open_measures)}</div>
                    <div className={styles.metricValue}>{reviewSummary.plannedOrOngoingMeasures}</div>
                  </Box>
                  <Box className={styles.metricCard}>
                    <div className={styles.metricLabel}>{t(messages.sections.summary.residual_risk)}</div>
                    <div className={styles.metricValue}>{t(ownerMessages.residual_risk[reviewSummary.residualRiskLevel])}</div>
                  </Box>
                </div>
                {reviewWarnings.length > 0 && (
                  <Box style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                    {reviewWarnings.map(warning => (
                      <Box
                        key={warning.id}
                        className={styles.errorBanner}
                        style={
                          warning.blocking
                            ? { backgroundColor: '#fff3f2', borderColor: '#efc1bf' }
                            : { backgroundColor: '#fffaf0', borderColor: '#f0d7a0' }
                        }
                      >
                        <Typography variant='body2'>{warning.message}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>{t(messages.sections.decision.eyebrow)}</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.decision.title)}
              </Typography>
              <Typography color='textSecondary' gutterBottom variant='body2'>
                {t(messages.sections.decision.help)}
              </Typography>

              {detail.status === 'approved' && (
                <Typography className={styles.pageHelp} variant='body2'>
                  {t(messages.sections.decision.approved_notice, {
                    date: formatTimestamp(detail.approvedAt),
                    name: detail.approvedByName ?? '—',
                  })}
                </Typography>
              )}

              {detail.status === 'clarification_requested' && (
                <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
                  {t(messages.sections.decision.awaiting_resubmission)}
                </Typography>
              )}

              {canDecide && (
                <>
                  <TextField
                    disabled={!canDecide}
                    error={Boolean(validationErrors.reviewComment)}
                    fullWidth
                    helperText={
                      validationErrors.reviewComment ||
                      (requiresReviewCommentForApproval(formValues.residualRiskLevel)
                        ? t(messages.sections.decision.review_comment_required_high_risk)
                        : t(messages.sections.decision.review_comment_help))
                    }
                    label={t(messages.sections.decision.review_comment)}
                    margin='normal'
                    multiline
                    onChange={event => {
                      setReviewComment(event.target.value);
                      setValidationErrors({});
                    }}
                    rows={4}
                    value={reviewComment}
                    variant='outlined'
                  />

                  {validationErrors.approval && (
                    <Typography color='error' gutterBottom variant='body2'>
                      {validationErrors.approval}
                    </Typography>
                  )}

                  {blockingErrors.length > 0 && (
                    <Box className={styles.errorBanner}>
                      {blockingErrors.map(error => (
                        <Typography key={error} color='error' variant='body2'>
                          {error}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
                    {t(messages.approval_notice)}
                  </Typography>

                  <div className={styles.reviewActions} style={{ justifyContent: 'flex-end', marginTop: 16 }}>
                    <ColoredButton
                      className=''
                      customColor='grey'
                      disabled={isRequestingClarification || isApproving}
                      icon={false}
                      onClick={handleClarificationClick}
                      showLoader={false}
                      textLike={false}
                      variant='outlined'
                    >
                      {t(messages.actions.request_clarification)}
                    </ColoredButton>
                    <ColoredButton
                      className=''
                      customColor='secondary'
                      disabled={changedAfterSubmission || blockingErrors.length > 0 || isApproving || isRequestingClarification}
                      icon={false}
                      onClick={handleApproveClick}
                      showLoader={isApproving}
                      textLike={false}
                      variant='contained'
                    >
                      {t(messages.actions.approve)}
                    </ColoredButton>
                  </div>
                </>
              )}

              {!canDecide && detail.reviewDecisionComment && (
                <TextField
                  disabled
                  fullWidth
                  label={t(messages.sections.decision.review_comment)}
                  margin='normal'
                  multiline
                  rows={4}
                  value={detail.reviewDecisionComment}
                  variant='outlined'
                />
              )}
            </Box>
          </section>
        </div>
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container maxWidth='lg'>
        <ManagePageHeader backButton noBorder onBack={navigateBack} subtitle={t(messages.page_subtitle)}>
          {detail?.title ?? t(messages.page_title)}
        </ManagePageHeader>
        {content}
      </Container>
    </ErrorBoundary>
  );
};

export default AdminDpiaReviewPage;
